/**
 * Who you are talking to. On a real frequency a controller says the callsign once and then keeps
 * working that aircraft; the session logs show players doing the same ("El Al 791, descend 3000"
 * … "heading 010") and the game answering "say again, who was that for?".
 *
 * This remembers the last aircraft addressed — by you or by the pilot calling you — so a
 * callsign-free continuation has somewhere to go. It is only ever consulted when no callsign was
 * heard at all: a callsign that was spoken but not matched must still fail, or a misheard one
 * would silently reach the wrong aircraft. Pure except for the clock; no DOM.
 */

/**
 * Assumption: 25 seconds is about one exchange. Long enough to answer a readback or correct
 * yourself, short enough that you are unlikely to have moved on to another aircraft.
 */
export const PARTNER_WINDOW_S = 25;

export interface ConversationPartner {
  /** Record that this aircraft was addressed, or called you. */
  note(callsign: string | null | undefined): void;
  /** The aircraft a callsign-free transmission belongs to, or null. */
  current(): string | null;
  /** Forget, e.g. when the session is paused or the aircraft leaves the frequency. */
  clear(): void;
}

export interface PartnerOptions {
  /** Whether the callsign is still on frequency; a handed-off or landed aircraft is not. */
  onFrequency(callsign: string): boolean;
  /** Seconds; injectable for tests. */
  now?: () => number;
}

export function createConversationPartner(options: PartnerOptions): ConversationPartner {
  const now = options.now ?? (() => Date.now() / 1000);
  let callsign: string | null = null;
  let atS = 0;
  return {
    note(cs) { if (cs) { callsign = cs; atS = now(); } },
    current() {
      if (!callsign) return null;
      if (now() - atS > PARTNER_WINDOW_S) return null;
      if (!options.onFrequency(callsign)) return null;
      return callsign;
    },
    clear() { callsign = null; },
  };
}
